import { Combobox } from '@headlessui/react';
import { TextField } from '@schuettflix/react-components';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFilterOrganizations, useOrganization } from '@/clients/organization/useOrganization.ts';

import { FilterOptionsOrganization, Organization as Org } from '@/clients/organization/Organization.ts';
import { useInternalRef } from '@/shared/hooks/useInternalRef.ts';
import { SearchOption } from '@/shared/components/SearchOption.tsx';
import Fuse from 'fuse.js';

type Organization = Pick<Org, 'id' | 'name' | 'billingAddress' | 'vatId' | 'taxId' | 'types'>;

type FilterableOrganizationProps = Pick<Organization, 'vatId' | 'taxId' | 'id'>;

type SpecialOption = {
    label: string;
    value: string;
};

type Option = Organization | SpecialOption;

export interface SelectOrganizationProps {
    className?: string;
    disabled?: boolean;
    specialOptions?: SpecialOption[];
    label?: string;
    value: Organization['id'] | SpecialOption['value'] | null;
    critical?: boolean;
    helperText?: string;
    onChange: (organizationId: Organization['id'] | SpecialOption['value'] | null) => void;
    filters: FilterOptionsOrganization;
    excludeMatchingIds?: Partial<FilterableOrganizationProps>;
    excludeIds?: Organization['id'][];
    title?: string;
    size?: 'sm' | 'md';
    placeholder?: string;
    selectedOrganizationId?: Organization['id'] | SpecialOption['value'] | undefined | null;
    dataTest?: string;
}

const filterOrganizationsByMatchingFields = (
    organizations: Organization[],
    excludeMatchingIds: Partial<FilterableOrganizationProps>
): Organization[] => {
    const keysFilter = Object.keys(excludeMatchingIds) as (keyof FilterableOrganizationProps)[];
    return organizations.filter(organization => !keysFilter.some(key => organization[key] === excludeMatchingIds[key]));
};

export const SelectOrganization = forwardRef<HTMLInputElement, SelectOrganizationProps>(
    (
        {
            filters,
            onChange,
            disabled,
            critical,
            helperText,
            label,
            specialOptions,
            excludeMatchingIds,
            excludeIds,
            size,
            placeholder,
            className,
            selectedOrganizationId,
            dataTest,
        },
        forwardRef
    ) => {
        const { t } = useTranslation();
        const ref = useInternalRef(forwardRef);

        const { data: selectedOrganization } = useOrganization(
            typeof selectedOrganizationId === 'number' ? selectedOrganizationId : undefined
        );

        const [currentOption, setCurrentOption] = useState<Option | null>(selectedOrganization || null);
        const [query, setQuery] = useState(selectedOrganization?.name ?? '');

        const fuse = useMemo(
            () =>
                new Fuse(specialOptions ?? [], {
                    keys: ['label'],
                    threshold: 0.4,
                }),
            [specialOptions]
        );

        const filteredSpecialOptions = useMemo(() => {
            if (!query) return specialOptions ?? [];
            const result = fuse.search(query);
            return result.map(res => res.item);
        }, [fuse, query, specialOptions]);

        useEffect(() => {
            if (selectedOrganization?.name && query !== selectedOrganization.name) {
                setQuery(selectedOrganization.name);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [selectedOrganization?.name]);

        useEffect(() => {
            if (!selectedOrganizationId) {
                return;
            }

            const matchingSpecialOption = specialOptions?.find(opt => opt.value === selectedOrganizationId);
            if (matchingSpecialOption) {
                setCurrentOption(matchingSpecialOption);
                setQuery(matchingSpecialOption.label);
            }
        }, [selectedOrganizationId, specialOptions]);

        const { data, isFetched } = useFilterOrganizations({
            ...filters,
            search: query,
        });

        let options: Organization[] = data ? data.items : [];

        // filter out platform organizations, there is no use case where the platform organization can be selected
        //  this is a quick fix since the organization list endpoint in the backend always includes the platform in
        //  the response; to avoid changing the endpoint for now since also a new filter needs to be added then
        //  the quickest solution is to hide the platform organizations here
        //  performance wise that’s not a big issue since there will be max 1 platform organization in the response
        options = options.filter(it => !it.types.includes('platform'));

        if (excludeMatchingIds) {
            options = filterOrganizationsByMatchingFields(options, excludeMatchingIds);
        }

        if (excludeIds?.length) {
            const excluded = new Set(excludeIds);
            options = options.filter(org => !excluded.has(org.id));
        }

        return (
            <div className={className}>
                <Combobox
                    value={currentOption}
                    onChange={option => {
                        if (!option) return;
                        if ('name' in option) {
                            setQuery(option.name);
                            setCurrentOption(option);
                            onChange(option.id);
                        } else {
                            setQuery(option.label);
                            setCurrentOption(option);
                            onChange(option.value);
                        }
                    }}
                    disabled={disabled}
                >
                    {/* https://github.com/tailwindlabs/headlessui/discussions/1236#discussioncomment-2970969 */}
                    <Combobox.Button as="div">
                        <Combobox.Input
                            title={query}
                            inputClassName="truncate"
                            data-test={dataTest}
                            as={TextField}
                            variant={size}
                            placeholder={placeholder}
                            ref={ref}
                            label={label || ''}
                            value={query}
                            displayValue={(org?: Organization) => org?.name ?? ''}
                            errorMessage={critical ? helperText : undefined}
                            onClick={() => {
                                ref.current?.select();
                            }}
                            onChange={event => {
                                const { value } = event.target;
                                setQuery(value);
                                if (value.length === 0) {
                                    onChange(null);
                                }
                            }}
                        />
                    </Combobox.Button>
                    <div className="relative" data-test="organization-list">
                        <Combobox.Options className="scroll font-copy-md bg-surface shadow-high absolute top-2 z-20 max-h-[250px] w-full overflow-y-auto rounded">
                            {filteredSpecialOptions?.length
                                ? filteredSpecialOptions.map(specialOption => (
                                      <Combobox.Option key={specialOption.value} value={specialOption}>
                                          {props => <SearchOption {...props} title={specialOption.label} />}
                                      </Combobox.Option>
                                  ))
                                : null}
                            {options.length === 0 && isFetched && filteredSpecialOptions.length === 0 ? (
                                <Combobox.Option value={null} disabled>
                                    <SearchOption
                                        active={false}
                                        selected={false}
                                        title={t('errorMessages.common.noResults') ?? ''}
                                    />
                                </Combobox.Option>
                            ) : (
                                options.map(org => (
                                    <Combobox.Option key={org.id} value={org} title={org.name}>
                                        {props => (
                                            <SearchOption
                                                {...props}
                                                title={org.name}
                                                description={
                                                    <address className="font-normal not-italic">{`${org.billingAddress.street} ${org.billingAddress.number}, ${org.billingAddress.zip} ${org.billingAddress.city}, ${org.billingAddress.country}`}</address>
                                                }
                                                side={org.id.toString()}
                                            />
                                        )}
                                    </Combobox.Option>
                                ))
                            )}
                        </Combobox.Options>
                    </div>
                </Combobox>
            </div>
        );
    }
);
